import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useWebSocket } from "../hooks/useWebSocket";
import { usePollingResource } from "../hooks/usePollingResource";
import CopilotWidget from "./CopilotWidget";
import { LayoutDashboard, TrendingUp, FlaskConical, Sliders, Activity, LogOut, Menu, X, Sparkles, BrainCircuit, Newspaper, Database, CalendarDays, ArrowLeftRight, GitBranch, Wallet, ReceiptText, Telescope, History, FileText, Gauge, Bell, Terminal, Shield, Download, } from "lucide-react";
interface MainNavItem {
    path: string;
    label: string;
    icon: any;
    prefix: string;
}
const MAIN_NAV_ITEMS: MainNavItem[] = [
    { path: "/", label: "홈", icon: LayoutDashboard, prefix: "/" },
    { path: "/operation", label: "운용", icon: TrendingUp, prefix: "/operation" },
    { path: "/research", label: "연구", icon: FlaskConical, prefix: "/research" },
    { path: "/management", label: "관리", icon: Sliders, prefix: "/management" },
];
const SECTION_LINKS = [
    { title: "개인 투자·트레이딩", links: [["/research/advisory", "정기 투자 리포트"], ["/research/personal", "AI 판단 메모"]] },
    { title: "데이터·LLM 분석", links: [["/management/intelligence", "분석 관제"], ["/management/data/news", "뉴스·근거"], ["/management/data/llm-intel", "추론 이력"], ["/management/data/saveticker", "데이터 탐색"], ["/management/data/calendar", "경제 일정"], ["/management/data/observability", "KIS·수급"]] },
    { title: "운용 판단", links: [["/operation/monitor", "전략·종목"], ["/operation/performance", "성과"], ["/operation/positions", "포지션"], ["/operation/trades", "거래"], ["/operation/invest", "ETF 투자북"]] },
    { title: "연구 근거", links: [["/research/forward", "전진 관찰"], ["/research/history", "과거 연구"], ["/research/schedule", "일정·문서"]] },
    { title: "실행·수집·제어", links: [["/management/status", "시스템 상태"], ["/management/control", "운용 제어"], ["/management/alerts", "알림"], ["/management/logs", "로그"], ["/management/risk", "리스크"], ["/management/data/models", "모델"], ["/management/data/export", "내보내기"]] },
];
const SECTION_ICONS: Record<string, typeof Activity> = {
    '/research/advisory': FileText, '/research/personal': BrainCircuit, '/management/intelligence': BrainCircuit,
    '/management/data/news': Newspaper, '/management/data/llm-intel': FileText,
    '/management/data/saveticker': Database, '/management/data/calendar': CalendarDays,
    '/management/data/observability': ArrowLeftRight, '/operation/monitor': GitBranch,
    '/operation/performance': TrendingUp, '/operation/positions': Wallet,
    '/operation/trades': ReceiptText, '/operation/invest': Sliders,
    '/research/forward': Telescope, '/research/history': History, '/research/schedule': CalendarDays,
    '/management/status': Gauge, '/management/control': Sliders, '/management/alerts': Bell,
    '/management/logs': Terminal, '/management/risk': Shield, '/management/data/models': BrainCircuit,
    '/management/data/export': Download,
};
export default function Layout({ children }: {
    children: React.ReactNode;
}) {
    const { logout } = useAuth();
    const { connected, latest } = useWebSocket();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [copilotOpen, setCopilotOpen] = useState(false);
    const sidebarRef = useRef<HTMLElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const media = window.matchMedia('(max-width: 900px)');
        const update = () => {
            if (media.matches && !sidebarOpen) sidebarRef.current?.setAttribute('inert', '');
            else sidebarRef.current?.removeAttribute('inert');
        };
        update(); media.addEventListener('change', update);
        return () => { media.removeEventListener('change', update); sidebarRef.current?.removeAttribute('inert'); };
    }, [sidebarOpen]);
    useEffect(() => {
        if (!sidebarOpen) return;
        const media = window.matchMedia('(max-width: 900px)');
        const sidebar = sidebarRef.current, body = bodyRef.current;
        if (!media.matches || !sidebar || !body) return;
        body.setAttribute('inert', ''); document.body.classList.add('sidebar-open');
        const focusables = () => [toggleRef.current, ...sidebar.querySelectorAll<HTMLElement>('a, button:not(:disabled)')].filter((element): element is HTMLElement => Boolean(element));
        const frame = requestAnimationFrame(() => (sidebar.querySelector<HTMLElement>('a[aria-current="page"]') || focusables()[1])?.focus());
        const keyboard = (event: KeyboardEvent) => {
            if (event.key === 'Escape') { event.preventDefault(); setSidebarOpen(false); }
            if (event.key === 'Tab') {
                const items = focusables(), first = items[0], last = items[items.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
            }
        };
        const resize = () => { if (!media.matches) setSidebarOpen(false); };
        document.addEventListener('keydown', keyboard); media.addEventListener('change', resize);
        return () => {
            cancelAnimationFrame(frame); document.removeEventListener('keydown', keyboard);
            media.removeEventListener('change', resize); body.removeAttribute('inert');
            document.body.classList.remove('sidebar-open'); toggleRef.current?.focus();
        };
    }, [sidebarOpen]);
    const profile = usePollingResource<{
        available: boolean;
        execution_model: string;
    }>("/positions/profile", 60000);
    const mode = profile.data?.available && !profile.error && profile.data.execution_model.includes("paper") ? "PAPER" : "모드 확인";
    const killSwitch = latest.risk_state?.kill_switch ?? false;
    const handleLogout = async () => {
        await logout();
        navigate("/");
    };
    const closeSidebar = () => setSidebarOpen(false);
    // Determine current active section for breadcrumb & highlight
    const currentPath = location.pathname;
    let currentNavLabel = "홈";
    if (currentPath.startsWith("/operation") ||
        ["/positions", "/trades", "/invest"].includes(currentPath)) {
        currentNavLabel = "운용";
    }
    else if (currentPath.startsWith("/research")) {
        currentNavLabel = "연구";
    }
    else if (currentPath.startsWith("/management") ||
        [
            "/risk",
            "/control",
            "/alerts",
            "/logs",
            "/news",
            "/calendar",
            "/saveticker",
            "/llm-intel",
            "/models",
            "/export",
        ].includes(currentPath)) {
        currentNavLabel = "관리";
    }
    const isNavActive = (item: MainNavItem) => {
        if (item.path === "/") {
            return currentPath === "/";
        }
        return currentNavLabel === item.label;
    };
    const detailLabel = SECTION_LINKS.flatMap(group => group.links).find(([path]) => path === currentPath)?.[1]
      ?? (currentPath.startsWith('/research/advisory/') ? '정기 투자 리포트 · 상세' : undefined);
    return (<div className="layout">
      <a className="skip-link" href="#main-content">본문으로 이동</a>
      <button className="sidebar-toggle" ref={toggleRef} aria-controls="site-sidebar" onClick={() => setSidebarOpen((v) => !v)} aria-label={sidebarOpen ? "사이드바 닫기" : "사이드바 열기"} aria-expanded={sidebarOpen}>
        {sidebarOpen ? <X size={20}/> : <Menu size={20}/>}
      </button>

      <div className={`sidebar-backdrop ${sidebarOpen ? "open" : ""}`} onClick={closeSidebar} aria-hidden="true"/>

      <aside id="site-sidebar" ref={sidebarRef} className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand-row">
            <h1>
              <Activity size={20} color="var(--accent)"/>
              <span>KORU</span>
            </h1>
            <span className="sidebar-live-pill">{mode}</span>
          </div>
          <span className="sidebar-subtitle">Control Tower · 퀀트 관제탑</span>
        </div>

        <nav className="sidebar-nav" aria-label="핵심 내비게이션">
          <div className="nav-group">
            <div className="nav-group-title">주요 메뉴</div>
            {MAIN_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isNavActive(item);
            return (<NavLink key={item.path} to={item.path} onClick={closeSidebar} className={`nav-item ${active ? "active" : ""}`}>
                  <Icon size={18}/>
                  <span>{item.label}</span>
                </NavLink>);
        })}
          </div>
          {SECTION_LINKS.map(group => <div className="nav-group nav-function-group" key={group.title}>
            <div className="nav-group-title">{group.title}</div>
            <div className="nav-function-links">{group.links.map(([path, label]) => {
              const Icon = SECTION_ICONS[path] ?? Activity;
              return <NavLink key={path} to={path} onClick={closeSidebar} className={({ isActive }) => `nav-function-link ${isActive ? 'active' : ''}`}><Icon size={15} aria-hidden="true"/><span>{label}</span></NavLink>;
            })}</div>
          </div>)}
        </nav>

        <div className="sidebar-footer">
          <div className={`ws-status ${connected ? "online" : "offline"}`}>
            <span className="ws-dot"/>
            <span>{connected ? "실시간 스트림 연결됨" : "재연결 중..."}</span>
          </div>
          {killSwitch && (<div className="kill-switch-warning">⚠ 킬스위치 가동 중</div>)}
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={15}/>
            <span>로그아웃</span>
          </button>
        </div>
      </aside>

      <div className="layout-body" ref={bodyRef}>
        <header className="cockpit-topbar">
          <div className="cockpit-topbar-left">
            <div className="cockpit-breadcrumb">
              <span className="breadcrumb-brand">KORU</span>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-current">{currentNavLabel}</span>
              {detailLabel && <><span className="breadcrumb-sep breadcrumb-detail">/</span><span className="breadcrumb-detail">{detailLabel}</span></>}
            </div>
          </div>
          <div className="cockpit-topbar-right">
            <span className="sidebar-live-pill">{mode}</span>
            <div className={`ws-status ${connected ? "online" : "offline"}`}>
              <span className="ws-dot"/>
              <span>{connected ? "연결됨" : "재연결"}</span>
            </div>
            <button className="btn-copilot-trigger" onClick={() => setCopilotOpen((v) => !v)} aria-label={copilotOpen ? "코파일럿 닫기" : "코파일럿 열기"} aria-expanded={copilotOpen}>
              <Sparkles size={14} color="var(--purple)"/>
              <span>도움 (AI)</span>
            </button>
          </div>
        </header>

        <main className="main-content" id="main-content" tabIndex={-1}>{children}</main>
      </div>

      <CopilotWidget open={copilotOpen} onOpenChange={setCopilotOpen} showFab={false}/>
    </div>);
}
